import { useState, useRef } from "react"
import { CheckCircle, XCircle, Upload, Plus, X, Pencil, Save, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Card, CardContent } from "@/modules/shared/ui/components/card"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Checkbox } from "@/modules/shared/ui/components/checkbox"
import { offboardingService } from "@/modules/offboarding/infrastructure/offboarding-service"
import type { OffboardingDocumentDto } from "@/modules/offboarding/dtos"

interface NewDocRow {
  key: string
  name: string
  file: File | null
  employeeEditable: boolean
}

interface Props {
  offboardingId: string
  organizationId: string
  documents: OffboardingDocumentDto[]
  isTerminal: boolean
  onRefresh: () => void
}

export function OffboardingDocumentsCard({ offboardingId, organizationId, documents, isTerminal, onRefresh }: Props) {
  const [isEditingDocs, setIsEditingDocs] = useState(false)
  const [isSavingDocs, setIsSavingDocs] = useState(false)
  const [editableDocs, setEditableDocs] = useState<Record<string, boolean>>({})
  // Case 1: uploading file to existing placeholder
  const [existingUploads, setExistingUploads] = useState<Record<string, { file: File | null; uploading: boolean }>>({})
  // Case 2: new extra documents with file
  const [newDocs, setNewDocs] = useState<NewDocRow[]>([])
  const [isSavingNewDocs, setIsSavingNewDocs] = useState(false)
  const existingFileRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const newFileRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const submittedCount = documents.filter((d) => d.submitted).length

  // Edit employeeEditable flags via PATCH
  const handleSaveDocuments = async () => {
    const updatedDocuments = documents.map(doc => ({
      id: doc.id,
      documentName: doc.documentName,
      employeeEditable: editableDocs[doc.id] ?? doc.employeeEditable,
    }))
    setIsSavingDocs(true)
    try {
      await offboardingService.patch(organizationId, offboardingId, { documents: updatedDocuments })
      toast.success("Documents updated")
      setIsEditingDocs(false)
      onRefresh()
    } catch {
      toast.error("Failed to update documents")
    } finally {
      setIsSavingDocs(false)
    }
  }

  // Case 1: fill existing placeholder
  const handleExistingUpload = async (doc: OffboardingDocumentDto) => {
    const entry = existingUploads[doc.id]
    if (!entry?.file) return
    setExistingUploads((p) => ({ ...p, [doc.id]: { ...p[doc.id], uploading: true } }))
    try {
      await offboardingService.uploadDocument(organizationId, offboardingId, entry.file, { existingDocumentId: doc.id })
      toast.success(`"${doc.documentName}" uploaded`)
      setExistingUploads((p) => { const n = { ...p }; delete n[doc.id]; return n })
      onRefresh()
    } catch {
      toast.error(`Failed to upload "${doc.documentName}"`)
      setExistingUploads((p) => ({ ...p, [doc.id]: { ...p[doc.id], uploading: false } }))
    }
  }

  // Case 2: upload new extra document with file
  const handleNewDocUpload = async (row: NewDocRow) => {
    if (!row.file || !row.name.trim()) return
    setNewDocs((p) => p.map((r) => r.key === row.key ? { ...r, uploading: true } : r))
    setIsSavingNewDocs(true)
    try {
      await offboardingService.uploadDocument(organizationId, offboardingId, row.file, {
        documentName: row.name.trim(),
        employeeEditable: row.employeeEditable,
      })
      toast.success(`"${row.name}" added`)
      setNewDocs((p) => p.filter((r) => r.key !== row.key))
      onRefresh()
    } catch {
      toast.error(`Failed to add "${row.name}"`)
    } finally {
      setIsSavingNewDocs(false)
    }
  }

  const handleView = async (docId: string, docName: string) => {
    try {
      const { sasUrl } = await offboardingService.downloadDocument(organizationId, offboardingId, docId)
      window.open(sasUrl, "_blank")
    } catch {
      toast.error(`Failed to view "${docName}"`)
    }
  }

  const handleDownload = async (docId: string, docName: string) => {
    try {
      const { sasUrl, fileName } = await offboardingService.downloadDocument(organizationId, offboardingId, docId)
      const a = document.createElement("a")
      a.href = sasUrl
      a.download = fileName || docName
      a.target = "_blank"
      a.rel = "noopener noreferrer"
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch {
      toast.error(`Failed to download "${docName}"`)
    }
  }

  const addNewDocRow = () => {
    setNewDocs((p) => [...p, { key: crypto.randomUUID(), name: "", file: null, employeeEditable: true }])
  }

  const removeNewDocRow = (key: string) => {
    setNewDocs(newDocs.filter((r) => r.key !== key))
  }

  return (
    <Card className="bg-card border border-border">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-foreground">Documents</h2>
            <span className="text-xs text-muted-foreground">{submittedCount}/{documents.length} submitted</span>
          </div>
          {isTerminal ? null : !isEditingDocs ? (
            <Button size="sm" variant="outline" onClick={() => {
              setEditableDocs(documents.reduce((acc, doc) => ({ ...acc, [doc.id]: doc.employeeEditable }), {}))
              setIsEditingDocs(true)
            }}>
              <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setIsEditingDocs(false)} disabled={isSavingDocs}>
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
              <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white border-0" onClick={handleSaveDocuments} disabled={isSavingDocs}>
                {isSavingDocs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" />Save</>}
              </Button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          {/* Existing placeholder documents — Case 1 */}
          {documents.map((doc) => {
            const entry = existingUploads[doc.id]
            return (
              <div key={doc.id} className="rounded-md border border-border bg-muted p-3 space-y-2">
                <div className="flex items-center gap-2">
                  {doc.submitted
                    ? <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                    : <XCircle className="w-4 h-4 text-muted-foreground shrink-0" />}
                  <span className="text-sm font-medium text-foreground flex-1 truncate">{doc.documentName}</span>
                  {doc.submitted && (
                    <>
                      <button type="button" onClick={() => handleView(doc.id, doc.documentName)} className="text-xs text-blue-500 hover:underline shrink-0">View</button>
                      <button type="button" onClick={() => handleDownload(doc.id, doc.documentName)} className="text-xs text-muted-foreground hover:underline shrink-0">Download</button>
                    </>
                  )}
                </div>
                {isEditingDocs && (
                  <div className="pl-6">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                      <Checkbox
                        checked={editableDocs[doc.id] ?? doc.employeeEditable}
                        onCheckedChange={(checked) => setEditableDocs(prev => ({ ...prev, [doc.id]: checked as boolean }))}
                      />
                      Employee Editable
                    </label>
                  </div>
                )}
                {!isTerminal && (
                  <div className="pl-6 flex items-center gap-2">
                    <input type="file" className="hidden" ref={(el) => { existingFileRefs.current[doc.id] = el }}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) setExistingUploads((p) => ({ ...p, [doc.id]: { file: f, uploading: false } })); e.target.value = "" }} />
                    <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={() => existingFileRefs.current[doc.id]?.click()}>
                      <Upload className="w-3 h-3 mr-1" />{entry?.file ? entry.file.name : doc.submitted ? "Replace" : "Upload"}
                    </Button>
                    {entry?.file && (
                      <Button type="button" size="sm" className="h-7 text-xs bg-orange-600 hover:bg-orange-700 text-white border-0" disabled={entry.uploading} onClick={() => handleExistingUpload(doc)}>
                        {entry.uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )
          })}

          {/* New extra documents — Case 2 */}
          {!isTerminal && newDocs.map((row) => (
            <div key={row.key} className="rounded-md border border-dashed border-border bg-muted p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-muted-foreground">New Document</p>
                <button type="button" onClick={() => removeNewDocRow(row.key)} className="text-muted-foreground hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
              </div>
              <Input
                placeholder="Document name e.g. Experience Letter"
                value={row.name}
                onChange={(e) => setNewDocs((p) => p.map((r) => r.key === row.key ? { ...r, name: e.target.value } : r))}
                className="h-8 text-sm"
              />
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <Checkbox
                  checked={row.employeeEditable}
                  onCheckedChange={(checked) => setNewDocs((p) => p.map((r) => r.key === row.key ? { ...r, employeeEditable: checked as boolean } : r))}
                />
                Employee Editable
              </label>
              <div className="flex items-center gap-2">
                <input type="file" className="hidden" ref={(el) => { newFileRefs.current[row.key] = el }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) setNewDocs((p) => p.map((r) => r.key === row.key ? { ...r, file: f } : r)); e.target.value = "" }} />
                <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={() => newFileRefs.current[row.key]?.click()}>
                  <Upload className="w-3 h-3 mr-1" />{row.file ? row.file.name : "Choose file"}
                </Button>
                {row.file && row.name.trim() && (
                  <Button type="button" size="sm" className="h-7 text-xs bg-orange-600 hover:bg-orange-700 text-white border-0" disabled={isSavingNewDocs} onClick={() => handleNewDocUpload(row)}>
                    {isSavingNewDocs ? <Loader2 className="w-3 h-3 animate-spin" /> : "Upload"}
                  </Button>
                )}
              </div>
            </div>
          ))}

          {!isTerminal && (
            <Button type="button" variant="outline" size="sm" className="w-full mt-1 border-dashed" onClick={addNewDocRow}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Add New Document
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
